import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Search } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { PageHero } from "@/components/PageHero";
import { WhatsAppActions } from "@/components/WhatsAppActions";
import { trackCtaClick, trackEvent } from "@/lib/analytics";
import { CATEGORIES } from "@/lib/blog-posts";
import { subscribeToNewsletter } from "@/lib/newsletter.functions";
import { useJournalArticles } from "@/hooks/useJournalArticles";
import type { ArticleItem } from "@/lib/journal-defaults";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Journal — Poonam Kapur Healthy Kitchen" },
      {
        name: "description",
        content:
          "Notes on seasonal eating, farm-direct sourcing and the kitchen. New writing from the Poonam Kapur team is on its way.",
      },
      { property: "og:title", content: "Journal — Poonam Kapur Healthy Kitchen" },
      {
        property: "og:description",
        content:
          "Essays, recipes and reflections from the Poonam Kapur team. First pieces coming soon.",
      },
      { property: "og:url", content: "https://poonamkapur.com/blog" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Journal — Poonam Kapur Healthy Kitchen" },
      {
        name: "twitter:description",
        content:
          "Essays, recipes and reflections from the Poonam Kapur team. First pieces coming soon.",
      },
    ],
    links: [{ rel: "canonical", href: "https://poonamkapur.com/blog" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Journal — Poonam Kapur Healthy Kitchen",
          url: "https://poonamkapur.com/blog",
          publisher: {
            "@type": "Organization",
            name: "Poonam Kapur Healthy Kitchen",
            url: "https://poonamkapur.com",
          },
        }),
      },
    ],
  }),
  component: BlogPage,
});


const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUCCESS_MSG =
  "Thank you. You'll receive an update when a new Journal article is published.";
const FAILURE_MSG =
  "We couldn't complete your subscription right now. Please try again later or contact us on WhatsApp.";

function BlogPage() {
  const subscribe = useServerFn(subscribeToNewsletter);
  const { articles, isFallback, isLoading } = useJournalArticles();
  const featured: ArticleItem | undefined = articles[0];
  const latest: ArticleItem[] = articles.slice(1);
  const showDemoNotice =
    isFallback || articles.some((a) => a.isDemo);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [status, setStatus] = useState<
    { kind: "success"; message: string } | { kind: "error"; message: string } | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    setStatus(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setFieldError("Please enter your email address.");
      return;
    }
    if (trimmed.length > 254) {
      setFieldError("Email must be 254 characters or fewer.");
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setFieldError("Please enter a valid email address.");
      return;
    }
    setFieldError(null);
    setIsSubmitting(true);
    trackCtaClick({ action: "newsletter_attempt", location: "journal-newsletter" });
    try {
      const result = await subscribe({ data: { email: trimmed, company } });
      setEmail("");
      setCompany("");
      setStatus({ kind: "success", message: SUCCESS_MSG });
      if (result?.stored === true) {
        trackEvent("newsletter_subscription_saved", { location: "journal-newsletter" });
      }
    } catch {
      setStatus({ kind: "error", message: FAILURE_MSG });
      trackEvent("newsletter_subscription_failed", { location: "journal-newsletter" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Journal"
        title="Notes from the kitchen."
        intro="Essays, recipes and reflections from the Poonam Kapur team — on seasonal eating, sourcing and how we cook."
      />

      {showDemoNotice && articles.length > 0 ? (
        <Section className="pt-8 pb-0">
          <div
            role="note"
            className="rounded-2xl border border-dashed border-primary/40 bg-primary/5 px-5 py-4 text-sm text-foreground/80"
          >
            <span className="font-medium text-foreground">Demo Content —</span>{" "}
            Some of the articles below are illustrative examples. They'll be
            replaced as the Poonam Kapur team publishes real writing.
          </div>
        </Section>
      ) : null}

      {/* Introduction */}
      <Section>
        <div className="max-w-3xl">
          <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">What to expect</span>
          <h2 className="mt-3 font-display text-3xl font-medium md:text-4xl">
            Honest writing about food.
          </h2>
          <p className="mt-5 leading-relaxed text-muted-foreground">
            Short pieces on seasonal eating in Mumbai, our farm-direct sourcing, hygienic kitchen
            practices and a nutrition-first approach — grounded in how we actually cook.
          </p>
        </div>

        {/* Search placeholder */}
        <div className="mt-10 max-w-xl">
          <label htmlFor="journal-search" className="sr-only">
            Search articles
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
              strokeWidth={1.75}
            />
            <input
              id="journal-search"
              type="search"
              disabled
              placeholder="Search articles…"
              aria-describedby="journal-search-help"
              className="w-full cursor-not-allowed rounded-full border border-border bg-secondary/40 py-3 pl-11 pr-4 text-sm text-muted-foreground placeholder:text-muted-foreground/70 disabled:opacity-70"
            />
          </div>
          <p id="journal-search-help" className="mt-2 text-xs text-muted-foreground">
            Search is disabled in this demo preview.
          </p>
        </div>

        {/* Categories */}
        <div className="mt-8">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Categories</div>
          <ul className="mt-3 flex flex-wrap gap-2" aria-label="Article categories (demo)">
            {CATEGORIES.map((c) => (
              <li key={c}>
                <span
                  aria-disabled="true"
                  className="inline-flex cursor-not-allowed items-center rounded-full border border-border bg-secondary/40 px-4 py-1.5 text-xs font-medium text-muted-foreground opacity-70"
                >
                  {c}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-muted-foreground">
            Category filtering is disabled in this demo preview.
          </p>
        </div>
      </Section>

      {/* Featured Article */}
      {featured ? (
      <Section className="bg-secondary/30">
        <div className="rounded-3xl border border-border bg-card p-6 md:p-10">
          <div className="grid gap-10 md:grid-cols-[1.1fr_1fr] md:items-center">
            <div>
              <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary">
                <span>Featured</span>
                {featured.isDemo ? (
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-foreground/70">
                    Demo Content
                  </span>
                ) : null}
                <span className="text-muted-foreground">{featured.category}</span>
              </div>
              <h2 className="mt-3 font-display text-3xl font-medium md:text-4xl">
                {featured.title}
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
                {featured.excerpt}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {featured.author ? <span>{featured.author}</span> : null}
                {featured.author && featured.date ? <span aria-hidden>·</span> : null}
                {featured.date ? <span>{featured.date}</span> : null}
                {featured.date && featured.readTime ? <span aria-hidden>·</span> : null}
                {featured.readTime ? <span>{featured.readTime}</span> : null}
              </div>
              <WhatsAppActions
                className="mt-8"
                location="journal-featured"
                compact
                chatLabel="Share on WhatsApp"
              />
              <div className="mt-4">
                <Link
                  to="/blog/$slug"
                  params={{ slug: featured.slug }}
                  className="inline-flex items-center rounded-full border border-border bg-background px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
                >
                  Read article →
                </Link>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl border border-border">
              <img
                src={featured.image}
                alt={featured.alt}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
              {featured.isDemo ? (
                <span className="absolute left-3 top-3 rounded-full bg-background/85 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.15em] text-foreground backdrop-blur">
                  Demo Content
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </Section>
      ) : null}

      {/* Latest Articles */}
      <Section>
        <div className="max-w-2xl">
          <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Latest</span>
          <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">Latest articles.</h2>
        </div>
        {articles.length === 0 && !isLoading ? (
          <div className="mt-10 rounded-3xl border border-dashed border-border bg-card p-10 text-center">
            <span className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-[11px] font-medium uppercase tracking-[0.15em] text-foreground/80">
              Coming Soon
            </span>
            <h3 className="mt-4 font-display text-2xl font-medium">
              New writing is on its way.
            </h3>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
              The first Journal pieces from the Poonam Kapur team will appear
              here shortly.
            </p>
          </div>
        ) : (
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {latest.map((post) => (
            <Link
              to="/blog/$slug"
              params={{ slug: post.slug }}
              key={post.id}
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-shadow duration-300 hover:shadow-sm"
            >
              <div className="relative">
                <img
                  src={post.image}
                  alt={post.alt}
                  loading="lazy"
                  className="aspect-[16/10] w-full object-cover"
                />
                {post.isDemo ? (
                  <span className="absolute left-3 top-3 rounded-full bg-background/85 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.15em] text-foreground backdrop-blur">
                    Demo Content
                  </span>
                ) : null}
              </div>
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  <span>{post.category}</span>
                </div>
                <h3 className="mt-3 font-display text-xl font-medium leading-snug group-hover:text-primary">
                  {post.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {post.excerpt}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {post.author ? <span>{post.author}</span> : null}
                  {post.author && post.date ? <span aria-hidden>·</span> : null}
                  {post.date ? <span>{post.date}</span> : null}
                  {post.date && post.readTime ? <span aria-hidden>·</span> : null}
                  {post.readTime ? <span>{post.readTime}</span> : null}
                </div>
              </div>
            </Link>
          ))}
        </div>
        )}
      </Section>

      {/* Newsletter signup */}
      <Section className="bg-secondary/30">
        <div className="grid gap-12 md:grid-cols-[1fr_1.2fr] md:items-start md:gap-16">
          <div>
            <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Newsletter</span>
            <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">
              Be first to read.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              A quiet note when a new piece goes live. No spam, no daily emails.
            </p>
          </div>
          <form
            onSubmit={handleSubscribe}
            noValidate
            className="rounded-3xl border border-border bg-card p-6 md:p-8"
            aria-describedby="newsletter-help"
          >
            <label htmlFor="newsletter-email" className="text-sm font-medium">
              Email address
            </label>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <input
                id="newsletter-email"
                type="email"
                required
                autoComplete="email"
                maxLength={254}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={fieldError ? true : undefined}
                aria-describedby={
                  fieldError ? "newsletter-email-error newsletter-help" : "newsletter-help"
                }
                disabled={isSubmitting}
                placeholder="you@example.com"
                className="flex-1 rounded-full border border-border bg-background px-5 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 disabled:opacity-70"
              />
              {/* Honeypot — must remain empty. Hidden from users and assistive tech. */}
              <input
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="hidden"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
                className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-sm transition-transform duration-300 hover:-translate-y-0.5 motion-reduce:transform-none disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
              >
                {isSubmitting ? "Subscribing…" : "Subscribe"}
              </button>
            </div>
            {fieldError && (
              <p
                id="newsletter-email-error"
                className="mt-2 text-xs text-destructive"
              >
                {fieldError}
              </p>
            )}
            <p id="newsletter-help" className="mt-3 text-xs text-muted-foreground">
              Subscription saves your email for future Journal updates. Automated emails are not yet enabled.
            </p>
            {status && (
              <p
                role={status.kind === "error" ? "alert" : "status"}
                aria-live="polite"
                className={`mt-4 rounded-2xl border p-4 text-sm ${
                  status.kind === "error"
                    ? "border-destructive/40 bg-destructive/5 text-foreground"
                    : "border-border bg-secondary/40 text-foreground"
                }`}
              >
                {status.message}
              </p>
            )}
          </form>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="pb-24">
        <div className="relative overflow-hidden rounded-[2rem] bg-primary px-10 py-20 text-primary-foreground md:px-16 md:py-28">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,color-mix(in_oklab,var(--accent)_35%,transparent),transparent_60%)]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,color-mix(in_oklab,var(--primary-foreground)_10%,transparent),transparent_55%)]"
          />
          <div className="relative max-w-2xl">
            <div className="mb-4 h-px w-10 bg-primary-foreground/50" aria-hidden />
            <span className="text-xs uppercase tracking-[0.2em] text-primary-foreground/80">Stay in the loop</span>
            <h2 className="mt-4 font-display text-4xl font-medium leading-tight md:text-6xl">
              Prefer real-time updates?
            </h2>
            <p className="mt-5 max-w-xl text-primary-foreground/90">
              Message us on WhatsApp and we'll let you know when new pieces go live.
            </p>
            <WhatsAppActions
              className="mt-10"
              location="journal-cta"
              tone="dark"
              compact
              chatLabel="Chat on WhatsApp"
            />
            <div className="mt-4">
              <Link
                to="/contact"
                onClick={() =>
                  trackCtaClick({ action: "contact", location: "journal-cta", href: "/contact" })
                }
                className="inline-flex items-center rounded-full border border-primary-foreground/40 bg-primary-foreground/5 px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-foreground/10"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </Section>
    </SiteLayout>
  );
}